// src/components/LoginSignup.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

function LoginSignup() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("student");
  const [classGroup, setClassGroup] = useState("C1");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  // const API_BASE_URL = "http://localhost:8000/api";
  const API_BASE_URL = "https://api.islorg.com/api";

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const clearFields = () => {
    setEmail("");
    setName("");
    setPassword("");
    setConfirmPassword("");
    setRole("student");
    setClassGroup("C1");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!email || !password) {
      alert("Enter both email and password");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.detail || "Login failed");
        setLoading(false);
        return;
      }

      const data = await response.json();
      localStorage.setItem("user", JSON.stringify({
        email,
        token: data.idToken,
        username: data.username,
        role: data.role
      }));
      clearFields();
      setLoading(false);
      navigate("/dashboard");
    } catch (error) {
      alert("Login error: " + error.message);
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!name || !email || !password || !confirmPassword || !role) {
      alert("Please fill all fields");
      return;
    }
    if (!email.endsWith("@islorg.com")) {
      alert("Email must be under domain islorg.com");
      return;
    }
    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    setLoading(true);

    const userData = {
      name,
      email,
      password,
      role,
      ...(role === "student" && { class: classGroup }),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.detail || "Signup failed");
        setLoading(false);
        return;
      }

      alert("Signup successful! Please ask admin to verify and approve your account.");
      clearFields();
      setLoading(false);
      setMode("login");
    } catch (error) {
      alert("Signup error: " + error.message);
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-purple-50 transition-all duration-300">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg transform transition-all duration-500 animate-fade-in">
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Adira Logo" className="w-12 h-12" />
        </div>
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-purple-700 mb-2">
            {mode === "login" ? "Login to Adira" : "Create an Adira Account"}
          </h1>
          <div className="flex justify-center gap-4 mt-4">
            <button
              className={`px-4 py-2 rounded transition ${mode === "login"
                ? "bg-purple-600 text-white"
                : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                }`}
              onClick={() => {
                clearFields();
                setMode("login");
              }}
              disabled={loading}
            >
              Login
            </button>
            <button
              className={`px-4 py-2 rounded transition ${mode === "signup"
                ? "bg-purple-600 text-white"
                : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                }`}
              onClick={() => {
                clearFields();
                setMode("signup");
              }}
              disabled={loading}
            >
              Signup
            </button>
          </div>
        </div>

        {mode === "login" ? (
          <form className="space-y-4" onSubmit={handleLogin}>
            <input
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
            <input
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <button
              className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition disabled:opacity-50"
              type="submit"
              disabled={loading}
            >
              {loading ? "Loading..." : "Continue"}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleSignup}>
            <input
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              required
            />
            <input
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              type="email"
              placeholder="Email (@islorg.com)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
            <select
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={loading}
              required
            >
              <option value="admin">Admin</option>
              <option value="teacher">Teacher</option>
              <option value="student">Student</option>
              <option value="staff">Staff</option>
            </select>
            {role === "student" && (
              <select
                className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
                value={classGroup}
                onChange={(e) => setClassGroup(e.target.value)}
                disabled={loading}
                required
              >
                <option value="C1">C1</option>
                <option value="C2">C2</option>
                <option value="C3">C3</option>
              </select>
            )}
            <input
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <input
              className="w-full border border-purple-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              required
            />
            <button
              className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition disabled:opacity-50"
              type="submit"
              disabled={loading}
            >
              {loading ? "Loading..." : "Signup"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginSignup;